/**
 * Kit Builder (Ignite / Shopify 2.0) — CartDrawer-safe (NO DOM swapping)
 * - Modes: Full Kit vs Build Your Kit
 * - Syncs "Select your purchase mode" radio (Full Kit vs Individual Components)
 * - Renders component tables from config.components (metafield custom.kit_builder_components)
 * - Adds parent kit + selected components via /cart/add.js (AJAX)
 * - Refreshes Ignite cart drawer via cartDrawer.reload() (best) or renderContents fallback
 * - Opens the cart drawer after update
 */
(function () {
  const ROOT_SELECTOR = "[data-kit-builder]";
  const TOGGLE_SELECTOR = "[data-kit-mode], .kit-toggle";
  const INIT_VERSION = "kb-v12";

  function initAll() {
    document.querySelectorAll(ROOT_SELECTOR).forEach(initOne);
  }

  function initOne(root) {
    if (!root) return;
    if (root.dataset.kitBuilderInit === INIT_VERSION) return;
    root.dataset.kitBuilderInit = INIT_VERSION;

    const cfgEl = root.querySelector("[data-kit-config]");
    if (!cfgEl) {
      console.warn("[KitBuilder] Missing [data-kit-config].");
      return;
    }

    let config = {};
    try {
      config = JSON.parse(cfgEl.textContent || "{}");
    } catch (e) {
      console.warn("[KitBuilder] Invalid JSON in [data-kit-config].", e);
      return;
    }

    const contentEl = root.querySelector("[data-kit-content]");
    const tablesEl = root.querySelector("[data-kit-tables]");
    const orderBodyEl = root.querySelector("[data-kit-order-body]");
    const subtotalEl = root.querySelector("[data-kit-subtotal]");
    const addBtn = root.querySelector("[data-kit-add]");
    const errorsEl = root.querySelector("[data-kit-errors]");

    if (!contentEl || !tablesEl || !orderBodyEl || !subtotalEl || !addBtn || !errorsEl) {
      console.warn("[KitBuilder] Missing expected DOM nodes. Check section markup.");
      return;
    }

    // ---------- State ----------
    const componentsRaw = Array.isArray(config.components) ? config.components : [];
    const components = componentsRaw.map(normalizeComponent).filter((c) => c.variantId);

    const state = {
      mode: "full", // "full" | "build"
      selected: new Map(), // variantId(string) -> qty(number)
      currency: config.currency || "NZD",
      components,
    };

    root.__kitSetMode = setMode;

    // ---------- Utils ----------
    function clearError() {
      errorsEl.textContent = "";
    }
    function setError(msg) {
      errorsEl.textContent = msg || "Something went wrong.";
    }
    function escapeHtml(str) {
      return String(str || "")
        .replaceAll("&", "&amp;")
        .replaceAll("<", "&lt;")
        .replaceAll(">", "&gt;")
        .replaceAll('"', "&quot;")
        .replaceAll("'", "&#039;");
    }

    function moneyFromCents(cents) {
      const num = Number(cents || 0) / 100;
      try {
        return num.toLocaleString(undefined, { style: "currency", currency: state.currency });
      } catch {
        return `$${num.toFixed(2)}`;
      }
    }

    function toCents(v) {
      const n = Number(v || 0);
      if (!Number.isFinite(n)) return 0;
      // If metafield is already cents (e.g. 38000) keep it
      if (!String(v).includes(".") && n >= 1000) return Math.round(n);
      if (String(v).includes(".")) return Math.round(n * 100);
      return Math.round(n);
    }

    function normalizeComponent(raw) {
      const variantId = Number(raw.variantId ?? raw.variantID ?? raw.variant_id ?? raw.id);
      return {
        heading: String(raw.heading ?? "Kit Components"),
        groupTitle: String(raw.groupTitle ?? raw.group ?? "Options"),
        ruleType: String(raw.ruleType ?? "or").toLowerCase() === "and" ? "and" : "or",
        required: Boolean(raw.required ?? false),

        variantId,
        productTitle: String(raw.productTitle ?? raw.title ?? raw.name ?? "Component"),
        variantTitle: String(raw.variantTitle ?? ""),
        sku: String(raw.sku ?? raw.partNumber ?? ""),
        priceCents: toCents(raw.price ?? 0),

        defaultQty: Number(raw.defaultQty ?? 0) || 0,
        minQty: Number(raw.minQty ?? (raw.required ? 1 : 0)) || 0,
        maxQty: Number(raw.maxQty ?? 99) || 99,
      };
    }

    // Pull parent variant ID from the *actual* product form (so it matches selected variant)
    function getParentVariantIdFromForm() {
      const productInfo = root.closest("product-info") || document.querySelector("product-info");
      const form =
        productInfo?.querySelector('form[action*="/cart/add"]') ||
        document.querySelector('form[action*="/cart/add"]');

      const idInput = form?.querySelector('input[name="id"]');
      const v = Number(idInput?.value || 0);
      return Number.isFinite(v) && v > 0 ? v : 0;
    }

    // ---------- Purchase Mode Sync ----------
    function findPurchaseModeFieldset() {
      const fieldsets = document.querySelectorAll("fieldset");
      for (const fs of fieldsets) {
        const legend = fs.querySelector("legend");
        if (!legend) continue;
        const t = (legend.textContent || "").trim().toLowerCase();
        if (t.includes("select your purchase mode")) return fs;
      }
      return null;
    }

    function setPurchaseModeRadio(targetLabel) {
      const fs = findPurchaseModeFieldset();
      if (!fs) return false;

      const inputs = fs.querySelectorAll('input[type="radio"]');
      for (const input of inputs) {
        const id = input.getAttribute("id");
        const label = id ? fs.querySelector(`label[for="${CSS.escape(id)}"]`) : null;
        const labelText = (label?.innerText || input.value || "").trim();

        if (labelText.toLowerCase() === String(targetLabel).trim().toLowerCase()) {
          if (!input.checked) {
            input.checked = true;
            input.dispatchEvent(new Event("change", { bubbles: true }));
            input.dispatchEvent(new Event("input", { bubbles: true }));
          }
          return true;
        }
      }
      return false;
    }

    // ---------- Rendering ----------
    function renderComponentTables() {
      tablesEl.innerHTML = "";

      if (!state.components.length) {
        tablesEl.innerHTML = `<p class="kit-builder__empty">No kit components configured.</p>`;
        return;
      }

      // Group: heading -> groupTitle -> items
      const byHeading = new Map();
      for (const c of state.components) {
        if (!byHeading.has(c.heading)) byHeading.set(c.heading, new Map());
        const byGroup = byHeading.get(c.heading);
        if (!byGroup.has(c.groupTitle)) byGroup.set(c.groupTitle, []);
        byGroup.get(c.groupTitle).push(c);
      }

      byHeading.forEach((groups, heading) => {
        const h = document.createElement("h2");
        h.className = "product-heading";
        h.textContent = heading;
        tablesEl.appendChild(h);

        groups.forEach((items, groupTitle) => {
          const header = document.createElement("div");
          header.className = "new-table product-header";
          header.innerHTML = `
            <div class="with-tooltip">
              <h3 class="product-type">${escapeHtml(groupTitle)}${items[0].required ? " *" : ""}</h3>
            </div>
          `;
          tablesEl.appendChild(header);

          const tbl = document.createElement("table");
          tbl.className = "product-table";
          const tbody = document.createElement("tbody");

          items.forEach((c) => {
            const initialQty = c.required
              ? Math.max(c.minQty, c.defaultQty || 0)
              : Math.max(0, c.defaultQty || 0);

            // seed selection map
            if (initialQty > 0) state.selected.set(String(c.variantId), initialQty);

            const tr = document.createElement("tr");
            tr.setAttribute("data-variant-id", String(c.variantId));
            tr.setAttribute("data-required", c.required ? "true" : "false");
            tr.innerHTML = `
              <td class="product-child-title" data-name="${escapeHtml(c.productTitle)}">
                ${escapeHtml(c.productTitle)}
                ${c.variantTitle ? `<div class="kit-variant">${escapeHtml(c.variantTitle)}</div>` : ""}
              </td>
              <td class="sm-hide">${escapeHtml(c.groupTitle)}</td>
              <td class="sm-hide">${escapeHtml(c.sku)}</td>
              <td class="item_price sm-hide" data-cents="${c.priceCents}">${moneyFromCents(c.priceCents)}</td>
              <td class="dp_price sm-hide">${moneyFromCents(c.priceCents * initialQty)}</td>
              <td>
                <div class="quantity-btn">
                  <div class="dec button">–</div>
                  <input type="number" min="${c.minQty}" max="${c.maxQty}" value="${initialQty}">
                  <div class="inc button">+</div>
                </div>
              </td>
            `;
            tbody.appendChild(tr);
          });

          tbl.appendChild(tbody);
          tablesEl.appendChild(tbl);
        });
      });
    }

    function renderOrderTable() {
      orderBodyEl.innerHTML = "";

      state.selected.forEach((qty, vid) => {
        const q = Number(qty || 0);
        if (!q || q <= 0) return;

        const c = state.components.find((x) => String(x.variantId) === String(vid));
        if (!c) return;

        const tr = document.createElement("tr");
        tr.innerHTML = `
          <td>${escapeHtml(c.productTitle)}</td>
          <td class="sm-hide">${escapeHtml(c.groupTitle)}</td>
          <td class="sm-hide">${escapeHtml(c.sku)}</td>
          <td class="sm-hide">${moneyFromCents(c.priceCents)}</td>
          <td class="sm-hide">${q}</td>
          <td>${moneyFromCents(c.priceCents * q)}</td>
        `;
        orderBodyEl.appendChild(tr);
      });
    }

    function calcSubtotalCents() {
      let sum = 0;
      state.selected.forEach((qty, vid) => {
        const q = Number(qty || 0);
        if (!q || q <= 0) return;

        const c = state.components.find((x) => String(x.variantId) === String(vid));
        if (!c) return;

        sum += c.priceCents * q;
      });
      return sum;
    }

    function requiredSatisfied() {
      const required = state.components.filter((c) => c.required);
      if (!required.length) return state.selected.size > 0;

      // basic “required components must be >= minQty”
      return required.every((c) => Number(state.selected.get(String(c.variantId)) || 0) >= c.minQty);
    }

    function updateSubtotalAndAddState() {
      subtotalEl.textContent = moneyFromCents(calcSubtotalCents());
      addBtn.disabled = state.mode === "build" ? !requiredSatisfied() : false;
    }

    function setMode(mode) {
      const next = mode === "build" ? "build" : "full";
      state.mode = next;

      // show/hide builder content
      contentEl.hidden = next !== "build";

      // sync variant option
      if (next === "build") setPurchaseModeRadio("Individual Components");
      else setPurchaseModeRadio("Full Kit");

      // toggle active class
      document.querySelectorAll(TOGGLE_SELECTOR).forEach((btn) => {
        const m = btn.getAttribute("data-kit-mode");
        if (!m) return;
        btn.classList.toggle("is-active", m === next);
        btn.setAttribute("aria-pressed", m === next ? "true" : "false");
      });

      // if switching to full kit, clear component quantities (keep required mins if you want)
      if (next === "full") {
        state.selected.clear();
        root.querySelectorAll('tr[data-variant-id] input[type="number"]').forEach((inp) => {
          const tr = inp.closest("tr[data-variant-id]");
          const isReq = tr?.getAttribute("data-required") === "true";
          const min = Number(inp.getAttribute("min") || 0);
          inp.value = isReq ? String(min) : "0";
          inp.dispatchEvent(new Event("change", { bubbles: true }));
          inp.dispatchEvent(new Event("input", { bubbles: true }));
        });
      }

      renderOrderTable();
      updateSubtotalAndAddState();
    }

    // ---------- Cart ----------
    // Theme: Dawn. Cart element: <cart-drawer>.
    // THE fix: remove the "is-empty" class Dawn stamps on page load,
    // then call drawer.open() — Dawn handles all fetching internally.
    // Do NOT call renderContents() — it crashes when called externally.

    async function addToCartAndRefresh(items) {
      // 1. Add items
      await addItemsAjax(items);

      // 2. Find the drawer
      const drawer = document.querySelector("cart-drawer");
      if (!drawer) {
        document.querySelector("#cart-icon-bubble")?.click();
        return;
      }

      // 3. Remove "is-empty" — this is the entire bug.
      //    Dawn's open() sees this class and renders the empty state instead of fetching.
      //    Stripping it forces Dawn to fetch and render the real cart.
      drawer.classList.remove("is-empty");

      // 4. Call Dawn's own open() — it will fetch /cart?sections=cart-drawer
      //    and inject the HTML itself, exactly as the native add-to-cart does.
      if (typeof drawer.open === "function") {
        try {
          drawer.open();
          return;
        } catch (e) {
          console.warn("[KitBuilder] drawer.open() failed:", e);
        }
      }

      // Fallback: click the header icon
      document.querySelector("#cart-icon-bubble")?.click();
    }

    async function addItemsAjax(items) {
      const res = await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ items }),
      });

      if (!res.ok) {
        const t = await res.text().catch(() => "");
        throw new Error(t || "Cart add failed");
      }
      return res.json();
    }

    // ---------- Events ----------
    root.addEventListener("click", (e) => {
      const btn = e.target.closest(".inc.button, .dec.button");
      if (!btn) return;

      const tr = btn.closest('tr[data-variant-id]');
      if (!tr) return;

      const input = tr.querySelector('input[type="number"]');
      if (!input) return;

      const min = Number(input.getAttribute("min") || 0);
      const max = Number(input.getAttribute("max") || 999);
      let val = Number(input.value || 0);

      if (btn.classList.contains("inc")) val += 1;
      if (btn.classList.contains("dec")) val -= 1;

      val = Math.max(min, Math.min(max, val));
      input.value = String(val);
      input.dispatchEvent(new Event("change", { bubbles: true }));
      input.dispatchEvent(new Event("input", { bubbles: true }));
    });

    root.addEventListener("change", (e) => {
      const input = e.target.closest('tr[data-variant-id] input[type="number"]');
      if (!input) return;

      const tr = input.closest('tr[data-variant-id]');
      const variantId = tr.getAttribute("data-variant-id");
      const qty = Number(input.value || 0);

      if (qty > 0) state.selected.set(String(variantId), qty);
      else state.selected.delete(String(variantId));

      const priceCents = Number(tr.querySelector(".item_price")?.getAttribute("data-cents") || 0);
      const totalCell = tr.querySelector(".dp_price");
      if (totalCell) totalCell.textContent = moneyFromCents(priceCents * qty);

      clearError();
      renderOrderTable();
      updateSubtotalAndAddState();
    });

    addBtn.addEventListener("click", async (e) => {
      e.preventDefault();
      e.stopPropagation();
      clearError();
      addBtn.disabled = true;

      try {
        const parentVariantId = getParentVariantIdFromForm();
        if (!parentVariantId) {
          setError("Could not detect the main kit variant ID.");
          return;
        }

        const items = [
          { id: parentVariantId, quantity: 1, properties: config.parent_properties || {} },
        ];

        if (state.mode === "build") {
          state.selected.forEach((qty, vid) => {
            const q = Number(qty || 0);
            if (!q || q <= 0) return;
            items.push({
              id: Number(vid),
              quantity: q,
              properties: {
                _kit_parent: config.parent_product_handle || "",
                _kit_parent_variant: String(parentVariantId),
              },
            });
          });
        }

        await addToCartAndRefresh(items);

      } catch (err) {
        console.warn("[KitBuilder] add error:", err);
        setError("Cart error. Please try again.");
      } finally {
        addBtn.disabled = false;
      }
    });

    // ---------- Init ----------
    renderComponentTables();
    renderOrderTable();
    updateSubtotalAndAddState();

    (function setInitialMode() {
      const fs = findPurchaseModeFieldset();
      if (fs) {
        const checked = fs.querySelector('input[type="radio"]:checked');
        const id = checked?.getAttribute("id");
        const label = id ? fs.querySelector(`label[for="${CSS.escape(id)}"]`) : null;
        const txt = (label?.innerText || checked?.value || "").trim().toLowerCase();
        if (txt.includes("individual")) return setMode("build");
      }
      setMode("full");
    })();

    console.log("[KitBuilder] init OK", { components: state.components.length });
  }

  // Toggle buttons (document-level so it works even if buttons are outside root)
  document.addEventListener("click", (e) => {
    const btn = e.target.closest(TOGGLE_SELECTOR);
    if (!btn) return;

    const mode = btn.getAttribute("data-kit-mode");
    if (mode !== "full" && mode !== "build") return;

    e.preventDefault();
    e.stopPropagation();

    const root = btn.closest(ROOT_SELECTOR) || document.querySelector(ROOT_SELECTOR);
    if (!root || typeof root.__kitSetMode !== "function") return;

    root.__kitSetMode(mode);
  });

  // Boot
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", initAll);
  else initAll();

  document.addEventListener("shopify:section:load", initAll);
  window.addEventListener("popstate", () => setTimeout(initAll, 50));
  document.addEventListener("visibilitychange", () => {
    if (!document.hidden) setTimeout(initAll, 50);
  });
})();
